<template>
  <div class="py-2 sm:px-4 sm:py-6">
    <!-- Search and Filter Bar -->
    <SourcingSearchBar v-model:search-query="searchQuery" v-model:show-completed="showCompleted" />

    <!-- Loading state -->
    <div v-if="isLoading || !isFullyLoaded" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <p class="text-red-800 dark:text-red-300">
        Fout bij laden van projecten: {{ error.message }}
      </p>
    </div>

    <!-- Empty state -->
    <div v-else-if="approvedProspects.length === 0" class="text-center py-12">
      <div class="mx-auto h-16 w-16 text-gray-400">
        <svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          ></path>
        </svg>
      </div>
      <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">
        Geen projecten om te concretiseren
      </h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Er zijn momenteel geen projecten die klaar zijn voor concretisering.
      </p>
      <router-link
        to="/beoordelen"
        class="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
      >
        Naar beoordelingslijst
        <svg class="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          ></path>
        </svg>
      </router-link>
    </div>

    <!-- Approved prospects list (Grouped by week) -->
    <div v-else class="space-y-6">
      <SourcingWeekGroup
        v-for="group in groupedByWeek"
        :key="group.weekKey"
        :week-key="group.weekKey"
        :week-label="group.weekLabel"
        :is-expanded="isWeekExpanded(group.weekKey)"
        @toggle="toggleWeekExpand(group.weekKey)"
      >
        <SourcingProspectCard
          v-for="prospect in group.items"
          :key="prospect.id"
          :prospect="prospect"
          :is-expanded="expandedProspects[prospect.id]"
          :is-fully-assigned="isProspectFullyAssigned(prospect)"
          :is-done="isProspectDone(prospect)"
          :assigned-count="getAssignedCount(prospect)"
          :role-count="getRoleCount(prospect)"
          :employee-names="getAssignedEmployeeNamesIncludingHistory(prospect.id)"
          :is-loading-preferences="
            isLoadingPreferences[prospect.id] || prospectPreferences[prospect.id] === undefined
          "
          @toggle="handleProspectToggle(prospect)"
          @reject="openRejectModal"
          @reassess="openReassessModal"
          @action="openActionModal"
        >
          <SourcingProspectDetails
            v-if="expandedProspects[prospect.id]"
            :prospect="prospect"
            :preferences="prospectPreferences[prospect.id] || []"
            :assignments="prospectAssignments[prospect.id] || []"
            :is-loading-preferences="isLoadingPreferences[prospect.id]"
            :is-fully-assigned="isProspectFullyAssigned(prospect)"
            :employees-map="employeesMap"
            :teams-map="teamsMap"
            :get-selected-employee-id-fn="
              (pref, prefIndex) => getSelectedEmployeeId(prospect.id, pref, prefIndex)
            "
            :get-custom-selected-employee-fn="
              (pref, prefIndex) => getCustomSelectedEmployee(prospect.id, pref, prefIndex)
            "
            :get-is-assigning-fn="
              (pref, prefIndex) => isAssigning[getSelectionKey(prospect.id, pref, prefIndex)]
            "
            :get-can-assign-fn="(pref, prefIndex) => canAssign(prospect.id, pref, prefIndex)"
            :get-expanded-graphs-fn="
              (pref, prefIndex) =>
                employeeGraphExpanded[`${prospect.id}-${pref.id || prefIndex}`] || {}
            "
            :get-is-availability-expanded-fn="
              (pref, prefIndex) => isAvailabilityExpanded(prospect.id, pref.id || prefIndex)
            "
            :get-match-stats-fn="() => prospectAvailability[prospect.id] || {}"
            :get-employee-availability-data-fn="() => employeeAvailabilityData[prospect.id] || {}"
            :get-other-proposals-fn="getOtherProposals"
            @select-employee="handleSelectEmployee(prospect, $event)"
            @assign="handleAssign(prospect, $event)"
            @remove-assignment="handleRemoveAssignment"
            @open-employee-selector="handleOpenEmployeeSelector(prospect, $event)"
            @clear-custom-employee="handleClearCustomEmployee(prospect, $event)"
            @toggle-availability="handleToggleAvailability(prospect, $event)"
            @toggle-employee-graph="handleToggleEmployeeGraph(prospect, $event)"
            @prospect-updated="handleProspectUpdated(prospect, $event)"
          />
        </SourcingProspectCard>
      </SourcingWeekGroup>
    </div>

    <!-- Employee Selection Modal -->
    <AssignEmployeeModal
      :open="showEmployeeModal"
      :project="selectedProspect || {}"
      :assigned-employee-ids="getAssignedEmployeeIds(selectedProspect?.id, selectedPreference)"
      :initial-team-id="getInitialTeamId()"
      :initial-function="getInitialFunction()"
      :initial-dpw="getInitialDpw()"
      :period-start="getPreferencePeriodStart()"
      :period-end="getPreferencePeriodEnd()"
      @close="closeEmployeeModal"
      @select="handleEmployeeSelected"
    />

    <!-- Action Assignment Modal -->
    <ActionAssignmentModal
      :open="showActionModal"
      :prospect="selectedProspectForAction"
      :default-assignee="{
        name: selectedProspectForAction?.submitted_by_name || '',
        email: selectedProspectForAction?.submitted_by_email || ''
      }"
      @close="closeActionModal"
      @created="handleActionCreated"
    />

    <!-- Rejection Modal -->
    <SourcingRejectModal
      :open="showRejectModal"
      :prospect="selectedProspectForReject"
      :is-submitting="isRejecting"
      :error="rejectError"
      @close="closeRejectModal"
      @submit="handleRejectSubmit"
    />

    <!-- Reassess Modal -->
    <SourcingReassessModal
      :open="showReassessModal"
      :prospect="selectedProspectForReassess"
      :is-submitting="isReassessing"
      :error="reassessError"
      @close="closeReassessModal"
      @submit="handleReassessSubmit"
    />
  </div>

  <!-- Remove assignment confirmation modal -->
  <ConfirmActionModal
    :open="showRemoveConfirm"
    title="Toewijzing verwijderen"
    message="Weet je zeker dat je deze toewijzing wilt verwijderen?"
    confirm-label="Verwijderen"
    variant="danger"
    @confirm="confirmRemoveAssignment"
    @cancel="showRemoveConfirm = false"
  />
</template>

<script setup>
import { ref, watch, watchEffect, onMounted } from 'vue'
import { toast } from '@/composables/ui/useToast'

// Components
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import AssignEmployeeModal from '@/components/common/modals/AssignEmployeeModal.vue'
import ActionAssignmentModal from '@/components/common/modals/ActionAssignmentModal.vue'
import {
  SourcingSearchBar,
  SourcingRejectModal,
  SourcingReassessModal,
  SourcingWeekGroup,
  SourcingProspectCard,
  SourcingProspectDetails
} from '@/components/sourcing'

// Composables
import { useProspectList } from '@/composables/data/useProspectList'
import { useProspectDataCache } from '@/composables/data/useProspectDataCache'
import { useSourcingAvailability } from '@/composables/domain/useSourcingAvailability'
import { useSourcingAssignment } from '@/composables/domain/useSourcingAssignment'
import { useSourcingUI } from '@/composables/domain/useSourcingUI'
import { useEmployeeSelectorModal } from '@/composables/modals/useEmployeeSelectorModal'
import { useRejectModal } from '@/composables/modals/useRejectModal'
import { useReassessModal } from '@/composables/modals/useReassessModal'
import { useActionModal } from '@/composables/modals/useActionModal'
import { useWeekGrouping } from '@/composables/domain/useWeekGrouping'

// ==================== Auto-refresh guard ====================
const isUserBusy = ref(false)

// ==================== Data Composables ====================
const { allProspects, isLoading, error, refetch, updateProspectInLocalState } = useProspectList({
  isUserBusy
})

const {
  approvedProspects,
  prospectPreferences,
  prospectAssignments,
  employeesMap,
  teamsMap,
  isFullyLoaded,
  isLoadingPreferences,
  searchQuery,
  showCompleted,
  loadPreferencesAndAssignments,
  reloadProspectData,
  reloadAssignmentsOnly,
  addEmployeeToMap,
  getPreferredEmployees,
  getAssignedEmployeeIds,
  getRoleCount,
  getAssignedCount,
  isProspectFullyAssigned,
  isProspectDone,
  getAssignedEmployeeNamesIncludingHistory
} = useProspectDataCache({ allProspects, includeReleasedAssignments: true })

// ==================== Availability Composable ====================
const {
  prospectAvailability,
  employeeAvailabilityData,
  loadAvailabilityForProspect,
  loadAvailabilityForEmployee
} = useSourcingAvailability(getPreferredEmployees)

// ==================== Assignment Composable ====================
const {
  selectedEmployees,
  customSelectedEmployees,
  isAssigning,
  getSelectionKey,
  canAssign,
  getOtherProposals,
  selectEmployee,
  clearCustomEmployee,
  preselectPlRecommended,
  assignEmployee,
  removeAssignment
} = useSourcingAssignment({
  prospectPreferences,
  prospectAssignments,
  approvedProspects,
  getPreferredEmployees,
  reloadProspectData: async prospectId => {
    await reloadProspectData(prospectId, {
      onPreferencesLoaded: async (pid, prefs) => {
        const prospect = approvedProspects.value.find(p => p.id === pid)
        if (prospect && prefs.length > 0) {
          await loadAvailabilityForProspect(prospect, prefs)
          preselectPlRecommended(pid, prefs)
        }
      }
    })
  }
})

// ==================== UI Composable ====================
const {
  expandedProspects,
  employeeGraphExpanded,
  toggleWeekExpand,
  isWeekExpanded,
  toggleExpand,
  collapseProspect,
  toggleAvailabilityExpanded,
  isAvailabilityExpanded,
  collapseAvailabilitySection,
  toggleEmployeeGraph
} = useSourcingUI()

// ==================== Week Grouping ====================
const { groupedByWeek } = useWeekGrouping(approvedProspects, {
  dateField: 'reviewed_at',
  fallbackDateField: 'start_date',
  sortFn: (a, b) => {
    // 0. Unsourced (niet-done) altijd boven sourced (done)
    const aDone = isProspectDone(a)
    const bDone = isProspectDone(b)
    if (aDone !== bDone) return aDone ? 1 : -1

    // Binnen beide groepen dezelfde sortering:
    // 1. Kans % DESC (hogere kans eerst)
    const chanceA = a.chance_to_score ?? 0
    const chanceB = b.chance_to_score ?? 0
    if (chanceA !== chanceB) return chanceB - chanceA

    // 2. Deal size DESC (hogere omzet eerst)
    const revA = a.expected_revenue || 0
    const revB = b.expected_revenue || 0
    if (revA !== revB) return revB - revA

    // 3. Top 20 account eerst
    if (a.is_top20 !== b.is_top20) return a.is_top20 ? -1 : 1

    // 4. Match status: goedgekeurd → te_bespreken → afgekeurd
    const statusOrder = { goedgekeurd: 0, te_bespreken: 1, afgekeurd: 2 }
    return (statusOrder[a.workflow_status] ?? 3) - (statusOrder[b.workflow_status] ?? 3)
  }
})

// ==================== Modal Composables ====================
const {
  showEmployeeModal,
  selectedProspect,
  selectedPreference,
  selectedPreferenceIndex,
  openEmployeeSelector,
  closeEmployeeModal,
  getInitialTeamId,
  getInitialFunction,
  getInitialDpw,
  getPreferencePeriodStart,
  getPreferencePeriodEnd
} = useEmployeeSelectorModal()

const {
  showRejectModal,
  selectedProspectForReject,
  isRejecting,
  rejectError,
  openRejectModal,
  closeRejectModal,
  handleRejectSubmit
} = useRejectModal({ refetch })

const {
  showReassessModal,
  selectedProspectForReassess,
  isReassessing,
  reassessError,
  openReassessModal,
  closeReassessModal,
  handleReassessSubmit
} = useReassessModal({ refetch })

const {
  showActionModal,
  selectedProspectForAction,
  openActionModal,
  closeActionModal,
  handleActionCreated
} = useActionModal({ approvedProspects, isProspectFullyAssigned, isFullyLoaded })

// ==================== Auto-refresh guard (update) ====================
// Pause auto-refetch when user is interacting with a prospect
watchEffect(() => {
  isUserBusy.value =
    Object.keys(expandedProspects.value).some(k => expandedProspects.value[k]) ||
    showEmployeeModal.value ||
    showRejectModal.value ||
    showReassessModal.value ||
    showActionModal.value
})

// ==================== Event Handlers ====================

// Handle prospect accordion toggle
function handleProspectToggle(prospect) {
  const isNowExpanded = toggleExpand(prospect.id)
  // Load data if expanding and not loaded yet
  if (isNowExpanded && !prospectPreferences.value[prospect.id]) {
    loadPreferencesAndAssignments(prospect.id, {
      onPreferencesLoaded: async (pid, prefs) => {
        if (prefs.length > 0) {
          await loadAvailabilityForProspect(prospect, prefs)
          preselectPlRecommended(pid, prefs)
        }
      }
    })
  }
}

// Selection key helper
function getSelectedEmployeeId(prospectId, pref, prefIndex) {
  const key = getSelectionKey(prospectId, pref, prefIndex)
  return selectedEmployees.value[key]
}

function getCustomSelectedEmployee(prospectId, pref, prefIndex) {
  const key = getSelectionKey(prospectId, pref, prefIndex)
  return customSelectedEmployees.value[key]
}

// Handle employee selection from radio buttons
function handleSelectEmployee(prospect, { pref, prefIndex, empId }) {
  selectEmployee(prospect.id, pref, prefIndex, empId)
}

// Handle assignment
async function handleAssign(prospect, { pref, prefIndex }) {
  try {
    await assignEmployee(prospect, pref, prefIndex)

    // Collapse the role (availability section) after successful assignment
    collapseAvailabilitySection(prospect.id, pref.id || prefIndex)

    // Check if all roles are now assigned, and if so, collapse the prospect
    // Use setTimeout to allow the data to refresh first
    setTimeout(() => {
      if (isProspectFullyAssigned(prospect)) {
        collapseProspect(prospect.id)
      }
    }, 100)
  } catch (_e) {
    toast.error(_e.response?.data?.error || 'Fout bij toewijzen van medewerker.')
  }
}

// Handle remove assignment
const showRemoveConfirm = ref(false)
const assignmentToRemove = ref(null)

function handleRemoveAssignment(assignment) {
  assignmentToRemove.value = assignment
  showRemoveConfirm.value = true
}

async function confirmRemoveAssignment() {
  showRemoveConfirm.value = false
  try {
    await removeAssignment(assignmentToRemove.value)
    toast.info('Toewijzing verwijderd.', { title: 'Verwijderd' })
  } catch (_e) {
    toast.error('Fout bij verwijderen van toewijzing.')
  }
  assignmentToRemove.value = null
}

// Handle open employee selector modal
function handleOpenEmployeeSelector(prospect, { pref, prefIndex }) {
  openEmployeeSelector(prospect, pref, prefIndex)
}

// Handle employee selected from modal
async function handleEmployeeSelected({ employee, _dpw }) {
  if (!selectedProspect.value || selectedPreference.value === null) return

  const pref = selectedPreference.value
  const prefIndex = selectedPreferenceIndex.value
  const prospectId = selectedProspect.value.id
  const key = getSelectionKey(prospectId, pref, prefIndex)

  // Add the employee to the preferred employees list
  const prefs = prospectPreferences.value[prospectId] || []
  const prefToUpdate = prefs.find(p => (p.id || prefs.indexOf(p)) === (pref.id || prefIndex))
  if (prefToUpdate) {
    let currentPreferred = getPreferredEmployees(prefToUpdate)
    if (!currentPreferred.includes(employee.simplicate_id)) {
      currentPreferred = [...currentPreferred, employee.simplicate_id]
      prefToUpdate.preferred_employees = currentPreferred
    }
  }

  // Add employee to map
  addEmployeeToMap(employee)

  // Clear custom selection and select this employee
  delete customSelectedEmployees.value[key]
  selectedEmployees.value[key] = employee.simplicate_id

  // Load availability for this employee
  await loadAvailabilityForEmployee(selectedProspect.value, pref, employee.simplicate_id)

  closeEmployeeModal()
}

// Handle clear custom employee
function handleClearCustomEmployee(prospect, { pref, prefIndex }) {
  clearCustomEmployee(prospect.id, pref, prefIndex)
}

// Handle toggle availability
function handleToggleAvailability(prospect, { pref, prefIndex }) {
  toggleAvailabilityExpanded(prospect.id, pref.id || prefIndex)
}

// Handle toggle employee graph
function handleToggleEmployeeGraph(prospect, { pref, prefIndex, empId }) {
  toggleEmployeeGraph(prospect.id, pref.id || prefIndex, empId)
}

// Handle prospect dates updated
async function handleProspectUpdated(prospect, updatedProspectData) {
  // Update the local state with the new dates
  updateProspectInLocalState(updatedProspectData)

  // Reload only assignments (they may have cascaded dates from the project)
  // Preferences don't change when dates change, so no need to reload them
  await reloadAssignmentsOnly(prospect.id)

  // Reload availability with the new date range
  const prefs = prospectPreferences.value[prospect.id]
  if (prefs && prefs.length > 0) {
    // Clear existing availability data to force refresh with new dates
    delete prospectAvailability.value[prospect.id]
    delete employeeAvailabilityData.value[prospect.id]

    // Get the updated prospect with new dates from local state
    const updatedProspect = approvedProspects.value.find(p => p.id === prospect.id)
    if (updatedProspect) {
      await loadAvailabilityForProspect(updatedProspect, prefs)
    }
  }
}

// ==================== Lifecycle & Watchers ====================

// Load preferences on initial mount for approved prospects
onMounted(() => {
  // The useSourcingData composable already handles loading on mount
  // This ensures we also load availability when preferences are loaded
})

// Watch for preferences becoming available and load availability data
// This watches prospectPreferences directly to avoid race with useSourcingData's watch on approvedProspects
watch(
  () => Object.keys(prospectPreferences.value),
  async (newKeys, oldKeys) => {
    // Find newly loaded preferences (keys that weren't there before)
    const oldSet = new Set(oldKeys || [])
    const newlyLoaded = newKeys.filter(key => !oldSet.has(key))

    for (const pidStr of newlyLoaded) {
      const pid = parseInt(pidStr)
      const prefs = prospectPreferences.value[pid]
      const prospect = approvedProspects.value.find(p => p.id === pid)

      if (prospect && prefs && prefs.length > 0 && !prospectAvailability.value[pid]) {
        await loadAvailabilityForProspect(prospect, prefs)
        preselectPlRecommended(pid, prefs)
      }
    }
  },
  { immediate: true }
)
</script>
